import getMediaSource from "../../utils/getMediaSource";
import { useAuth0 } from "@auth0/auth0-react";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import { FC, memo, useEffect, useState } from "react";
import {
  LazyLoadImage,
  LazyLoadImageProps,
} from "react-lazy-load-image-component";
import type { MediaParams } from "~@/api";
import avatarPlaceholderOperator from "~@/assets/image/non-svg/undefined2.jpg";
import avatarPlaceholderClient from "~@/assets/image/non-svg/undefined.jpg";
import { PresenceStatus } from "~@/graphql/codegen/generated";

export enum AvatarPreference {
  Rounded = "avatar--is-rounded",
  Editable = "avatar--is-editable",
}

type AvatarProps = {
  mediaParams: Omit<MediaParams, "type" | "id">;
  preferences?: AvatarPreference[];
  isClient?: boolean;
} & (
  | { hasStatus?: true; presenceStatus: PresenceStatus }
  | { hasStatus?: false; presenceStatus?: undefined }
) &
  Omit<
    LazyLoadImageProps,
    "src" | "width" | "height" | "onError" | "className"
  >;

const Avatar: FC<AvatarProps> = ({
  mediaParams: { url, width, height, quality, upscale },
  preferences,
  hasStatus = false,
  isClient = false,
  presenceStatus,
  ...props
}) => {
  const [token, setToken] = useState("");

  const { getAccessTokenSilently } = useAuth0();
  useEffect(() => {
    getAccessTokenSilently().then((r) => setToken(r));
  }, [getAccessTokenSilently]);

  return (
    <figure
      className={classNames("avatar", preferences, {
        "avatar--has-status-online": presenceStatus === PresenceStatus.Online,
        "avatar--has-status-away": presenceStatus === PresenceStatus.Away,
        "avatar--has-status-offline": presenceStatus === PresenceStatus.Offline,
      })}
    >
      <LazyLoadImage
        src={
          url && !url.includes("undefined.jpg")
            ? getMediaSource({
                width: window.innerWidth,
                height: window.innerHeight,
                type: "userpic",
                token,
                quality,
                upscale,
                url,
              })
            : isClient
            ? avatarPlaceholderClient
            : avatarPlaceholderOperator
        }
        width={width}
        height={height}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = isClient
            ? avatarPlaceholderClient
            : avatarPlaceholderOperator;
        }}
        effect="blur"
        alt="user avatar"
        {...props}
      />
    </figure>
  );
};

export default memo(Avatar, isEqual);
